import React, { useState, useContext } from 'react';
import { IconButton, Tooltip, TextField, DialogContentText } from '@mui/material';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { PackageEntryStatus } from '../../../types/packager-types';
import usePackageEntryUpdate from '../../hooks/packages/usePackageEntryUpdate';
import { useQueryClient } from '@tanstack/react-query';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';
import { AppContext } from '../../../App';
import { extractApiError } from '../../../utils/extractApiError';

export interface IPackageEntryStatusChangeProps {
  entryId: string;
  currentStatus: PackageEntryStatus;
  // Set when the package's status refuses entry edits (the API would 409).
  disabledReason?: string | null;
}

const PackageEntryStatusChange: React.FC<IPackageEntryStatusChangeProps> = ({
  entryId,
  currentStatus,
  disabledReason,
}) => {
  const { setToast } = useContext(AppContext);
  const queryClient = useQueryClient();
  const [pendingAction, setPendingAction] = useState<'INCLUDE' | 'EXCLUDE' | null>(null);
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [exclusionReason, setExclusionReason] = useState<string>('');

  const isIncluded = currentStatus === PackageEntryStatus.INCLUDED;
  const isExcluded = currentStatus === PackageEntryStatus.EXCLUDED;

  const title = disabledReason || (isIncluded ? 'Exclude from package' : 'Include in package');

  const { mutate: updateEntryStatus, isPending: isLoading } = usePackageEntryUpdate({
    onSuccess: () => {
      const message =
        pendingAction === 'EXCLUDE' ? 'Entry excluded from package' : 'Entry included in package';
      setToast({ variant: 'success', open: true, message });
      queryClient.invalidateQueries({
        queryKey: ['packageEntries']
      });
      queryClient.invalidateQueries({
        queryKey: ['allPackageEntries']
      });
      // The edit moves the package to DRAFT and marks its files stale.
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      queryClient.invalidateQueries({ queryKey: ['package'] });
      setPendingAction(null);
    },
    onError: (error: any) => {
      setToast({
        variant: 'error',
        open: true,
        message: extractApiError(error, 'Failed to update package entry status'),
      });
      console.error('Failed to update package entry status:', error);
      setPendingAction(null);
    },
  });

  // After the hooks: an entry can move to TRANSMISSION_* between renders, and an
  // early return above them changes the hook order.
  if (!isIncluded && !isExcluded) return null;

  const handleClick = () => {
    if (isIncluded) {
      setExcludeDialogOpen(true);
      setPendingAction('EXCLUDE');
      return;
    }
    setPendingAction('INCLUDE');
    updateEntryStatus({ id: entryId, status_in_package: PackageEntryStatus.INCLUDED });
  };

  const handleCloseExcludeDialog = () => {
    setExcludeDialogOpen(false);
    setExclusionReason('');
  };

  const handleConfirmExclude = () => {
    setPendingAction('EXCLUDE');
    updateEntryStatus({
      id: entryId,
      status_in_package: PackageEntryStatus.EXCLUDED,
      exclusion_reason: exclusionReason || null,
    });
    handleCloseExcludeDialog();
  };

  return (
    <>
      <Tooltip title={title} placement="top">
        <span>
          <IconButton
            size="small"
            onClick={handleClick}
            aria-label={title}
            disabled={isLoading || !!disabledReason}
          >
            {isIncluded ? (
              <DoDisturbAltIcon fontSize="small" />
            ) : (
              <AddCircleOutlineIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <DialogGlynt
        open={excludeDialogOpen}
        closeModal={handleCloseExcludeDialog}
        cypressId="dialog-exclude-entry"
        width="500px"
      >
        <DialogTitleGlynt title="Exclude Package Entry" closeModal={handleCloseExcludeDialog} />
        <DialogContentGlynt>
          <DialogContentText sx={{ padding: '10px 3px' }}>
            <FormControlStyled sx={{ mt: 0, width: '100%' }}>
              <FormLabelStyled>Reason (optional)</FormLabelStyled>
              <TextField
                id="exclusion-reason"
                size="small"
                variant="outlined"
                placeholder="Enter a reason (e.g., DUPLICATE_MANUAL)"
                value={exclusionReason}
                onChange={(e) => setExclusionReason(e.target.value)}
                fullWidth
              />
            </FormControlStyled>
          </DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          submitButtonText="Exclude"
          closeModal={handleCloseExcludeDialog}
          handleSubmit={handleConfirmExclude}
          cancelCypressId="btn-exclude-entry-cancel"
          submitCypressId="btn-exclude-entry-submit"
          clearCypressId="btn-exclude-entry-clear"
          submitDisabled={isLoading}
        />
      </DialogGlynt>
    </>
  );
};

export default PackageEntryStatusChange;
